<template>
  <el-container class="vivopda-container">
    <!-- 页面头部 -->
    <el-header class="page-header">
      <div class="header-content">
        <div class="header-title">
          <h1>VIVO 批量搭建</h1>
          <span class="header-version">版本号: v2.0.0</span>
        </div>
        <!-- <div class="header-right">
          <el-tag v-if="ids_list.length > 0" type="success" effect="dark" size="medium">
            已加载 {{ ids_list.length }} 个账户
          </el-tag>
        </div> -->
      </div>
    </el-header>

    <!-- 步骤条 - 悬浮显示任务进度 -->
    <transition name="slide-fade"> 
      <div class="ad-flow" v-if="showProgress">
        <div class="ad-flow-header">
          <i class="el-icon-loading"></i>
          <span>任务进度</span>
        </div>
        <el-steps :active="activeStep" finish-status="success" direction="vertical" align-center>
          <el-step v-for="(item, index) in steps" :key="index" :title="item.name" :status="getStepStatus(item.status)">
            <template #description>
              <div class="step-detail">{{ item.detail }}</div>
            </template>
          </el-step>
        </el-steps>
        <div class="progress-percent">
          <el-progress :percentage="uploadPercent" :stroke-width="8" :color="customColors" />
        </div>
      </div>
    </transition>

    <el-main class="page-main">
      <div class="page-layout">
        <!-- 左侧快捷导航 -->
        <div class="quick-nav">
          <div class="nav-title">快速导航</div>
          <div class="nav-list">
            <div v-for="item in navItems" :key="item.ref"
              class="nav-item" :class="{ active: item.active }"
              @click="scrollToSection(item.ref)">
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
            </div>
          </div>
        </div>
        <div class="nav-placeholder"></div>

        <!-- 右侧内容区 -->
        <div class="plan-container">
        <!-- 任务列表卡片 -->
        <transition-group name="list-enter" tag="div">
          <el-card class="task-card" v-if="tasks.length > 0" key="task-card" shadow="hover">
            <div slot="header" class="task-header">
              <div class="task-header-left">
                <i class="el-icon-s-order"></i>
                <span class="task-header-title">批量任务列表</span>
                <el-badge :value="tasks.filter(t => t.status === 'running').length" class="task-badge"
                  v-if="tasks.some(t => t.status === 'running')">
                  <span style="display:none;">.</span>
                </el-badge>
              </div>
              <div class="task-header-actions">
                <el-button type="text" @click="openTaskListDialog">
                  <i class="el-icon-view"></i> 查看全部
                </el-button>
                <el-button type="text" @click="clearCompletedTasks">
                  <i class="el-icon-delete"></i> 清除已完成
                </el-button>
              </div>
            </div>
            <div class="task-list">
              <div v-for="task in tasks.slice(0, 3)" :key="task.id" class="task-item">
                <div class="task-title">
                  <el-row type="flex" align="middle">
                    <el-col :span="14">
                      <i class="el-icon-document task-item-icon"></i>
                      <span class="task-name">{{ task.name }}</span>
                      <el-tag :type="getTaskStatusType(task.status)" effect="plain" class="status-tag">
                        {{ getTaskStatusText(task.status) }}
                      </el-tag>
                    </el-col>
                    <el-col :span="10" class="task-actions">
                      <el-button v-if="task.status === 'running'" @click="stopTask(task.id)" size="mini" type="warning"
                        plain round>停止</el-button>
                      <el-button v-if="task.status === 'running'" @click="pauseTask(task.id)" size="mini" type="info"
                        plain round>暂停</el-button>
                      <el-button v-if="task.status === 'paused'" @click="resumeTask(task.id)" size="mini" type="success"
                        plain round>继续</el-button>
                    </el-col>
                  </el-row>
                </div>
                <div class="task-progress">
                  <span class="task-progress-label">进度: {{ task.currentIndex }}/{{ task.totalItems }}</span>
                  <el-progress :percentage="Math.round(task.currentIndex / task.totalItems * 100)"
                    :status="getProgressStatus(task.status)" :stroke-width="6">
                  </el-progress>
                </div>
              </div>
            </div>
          </el-card>
        </transition-group>

        <!-- 主表单 -->
        <el-form :model="form" label-position="right" class="vivopda-form">

          <!-- ==================== 账户信息 ==================== -->
          <div class="section-block" ref="sectionAccount">
            <div class="section-title section-title-primary">
              <i class="el-icon-user-solid section-icon"></i>
              账户信息
            </div>
            <div class="section-body">
              <el-form-item label="账户名称" label-width="140px">
                <div class="account-input-area">
                  <el-input type="textarea" v-model="username" placeholder="请输入账户名称，每行一个" :rows="4"
                    class="account-textarea" />
                  <el-select v-model="selectedRegion" placeholder="请选择区域" class="region-select"
                    @change="handleRegionChange">
                    <el-option v-for="item in regionOptions" :key="item.value" :label="item.label"
                      :value="item.value" />
                  </el-select>
                  <el-button type="primary" icon="el-icon-search" @click="findIds()" class="query-btn">查 询</el-button>
                </div>
                <div class="account-tags" v-if="ids_list.length > 0">
                  <el-tag v-for="(item, index) in ids_list" :key="index"  effect="plain" closable
                    @close="removeAccount(index)">
                    {{ item.twoname }}
                  </el-tag>
                </div>
              </el-form-item>
            </div>
          </div>

          <!-- ==================== 广告计划 ==================== -->
          <div class="section-block" ref="sectionPlan">
            <div class="section-title section-title-primary">
              <i class="el-icon-s-flag section-icon"></i>
              广告计划
            </div>
            <div class="section-body">
              <div class="subsection-title">计划设置</div>

              <el-form-item label="推广目标" label-width="140px">
                <el-radio-group v-model="form.extension_type" @input="extensionTypeChange">
                  <el-radio-button v-for="item in extensionType_goal_group" :key="item.value" :label="item.value">{{
                    item.label }}</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <el-form-item label="投放渠道" label-width="140px">
                <el-radio-group v-model="form.mediaType">
                  <el-radio-button v-for="item in availableMediaTypes" :key="item.value" :label="item.value">{{
                    item.label
                    }}</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <el-form-item v-if="showPromotionType" label="场景定制" label-width="140px">
                <el-radio-group v-model="form.promotionType" >
                  <el-radio-button :label="0">普通投放</el-radio-button>
                  <el-radio-button v-if="promotionTypeHasZhito" :label="1">智投</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <el-form-item v-if="showCampaignType" label="广告类型" label-width="140px">
                <el-radio-group v-model="form.campaignType"  disabled>
                  <el-radio-button :label="0">展示广告</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <el-form-item v-if="showConversionMonitorType" label="转化监测" label-width="140px">
                <el-radio-group v-model="form.conversionMonitorType">
                  <el-radio-button :label="0">普通</el-radio-button>
                  <el-radio-button :label="1">增效</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <el-form-item label="计划预算" label-width="140px">
                <div>
                  <el-radio-group v-model="form.budgetType">
                    <el-radio-button :label="0">不限</el-radio-button>
                    <el-radio-button :label="1">自定义</el-radio-button>
                  </el-radio-group>
                  <div v-if="form.budgetType === 1" style="margin-top: 10px;">
                    <el-input v-model="form.dailyBudget" placeholder="请输入预算金额" class="input-short">
                      <template slot="append">元/天</template>
                    </el-input>
                  </div>
                </div>
              </el-form-item>

              <el-form-item label="计划名称" label-width="140px">
                <el-input v-model="form.planName" placeholder="请输入计划名称" :rows="3" class="input-large" />
              </el-form-item>

              <el-form-item label="计划开关" label-width="140px">
                <el-switch v-model="form.planSwitch" active-color="#13ce66" inactive-color="#ff4949" />
                <span class="switch-label">{{ form.planSwitch ? '开启' : '关闭' }}</span>
              </el-form-item>

              <el-form-item label="最新计划" label-width="140px">
                <el-switch v-model="form.latestPlan" active-color="#13ce66" inactive-color="#ff4949" />
                <span class="switch-label">{{ form.latestPlan ? '开启' : '关闭' }}</span>
              </el-form-item>

            </div>
          </div>

          <!-- ==================== 广告组 ==================== -->
          <div class="section-block" ref="sectionGroup">
            <div class="section-title section-title-primary">
              <i class="el-icon-s-grid section-icon"></i>
              广告组
            </div>
            <div class="section-body">
              <el-form-item label="行业资质" label-width="140px">
                <el-cascader
                  v-if="qualificationOptions.length > 0"
                  v-model="form.qualification"
                  :options="qualificationOptions"
                  :props="{ expandTrigger: 'hover' }"
                  placeholder="请选择行业资质"
                  clearable
                  class="input-large"
                />
                <el-button type="text" size="small" @click="queryQualification" :loading="qualificationLoading" :disabled="ids_list.length === 0" style="margin-left: 8px;">刷新资质</el-button>
              </el-form-item>

              <el-form-item label="微信关注" label-width="140px">
                <el-switch v-model="form.wechatFollow" active-color="#13ce66" inactive-color="#ff4949" />
                <span class="switch-label">{{ form.wechatFollow ? '开启' : '关闭' }}</span>
              </el-form-item>
              <el-form-item label="搭建数量" label-width="140px">
                <el-input-number v-model="form.adgroupNumber" :min="1" />
                <span class="form-tip">设置广告组搭建数量</span>
              </el-form-item>

              <div class="subsection-title">转化与出价</div>

              <el-form-item label="转化目标" label-width="140px">
                <el-select v-model="form.cvType" placeholder="请选择转化目标" class="input-medium">
                  <el-option v-for="item in cvOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </el-form-item>

              <el-form-item label="二阶出价" label-width="140px">
                <el-switch v-model="form.useOldPrice" active-text="使用最新广告组出价" />
                <el-input v-model="form.ocpxPrice" placeholder="出价" class="input-mini" style="margin-left: 16px;" />
                <span class="input-suffix">元</span>
              </el-form-item>

              

              <el-form-item label="人群包ID" label-width="140px">
                <el-input v-model="form.audienceIds" placeholder="多个用英文逗号分隔" class="input-medium" />
              </el-form-item>

              <el-form-item label="排除人群包" label-width="140px">
                <el-input v-model="form.excludeAudienceIds" placeholder="多个用英文逗号分隔" class="input-medium" />
              </el-form-item>

              <div class="subsection-title">投放时段</div>

              <el-form-item label="投放时间段" label-width="140px">
                <el-radio-group v-model="form.timeSettingType">
                  <el-radio-button :label="0">不限</el-radio-button>
                  <el-radio-button :label="1">自定义</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <div v-if="form.timeSettingType === 1" class="time-grid-form-item">
                <div class="time-grid-wrapper">
                  <div class="grid-toolbar">
                    <span class="grid-tip"><i class="el-icon-info"></i> 点击或拖动鼠标选择时段</span>
                    <el-button type="text" @click="clearTimeGrid" style="color: #f56c6c;">清空全部</el-button>
                  </div>
                  <div class="grid-container">
                    <table class="grid-table">
                      <thead>
                        <tr>
                          <th class="weekday-header">星期/小时</th>
                          <th v-for="h in 24" :key="h" class="hour-header">{{ h - 1 }}</th>
                        </tr>
                      </thead>
                      <tbody @mouseup="endDragSelect" @mouseleave="endDragSelect">
                        <tr v-for="(day, dIdx) in weekDays" :key="dIdx">
                          <td class="weekday-cell">{{ day }}</td>
                          <td v-for="h in 24" :key="`${dIdx}_${h}`"
                            :class="['time-cell', { active: timeGrid[`${dIdx}_${h}`] }]"
                            @mousedown.prevent="startDragSelect(dIdx, h, $event)"
                            @mouseenter="onDragEnter(dIdx, h)"
                          ></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div class="slot-summary" v-if="hasSelectedTimes">
                    <div class="summary-title">时段汇总：</div>
                    <div class="summary-list">
                      <div v-for="item in timeSummary" :key="item.weekday" class="summary-item">
                        <span class="summary-weekday">{{ item.weekday }}：</span>
                        <span class="summary-hours">{{ item.hours }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <el-form-item label="投放日期" label-width="140px">
                <el-date-picker v-model="form.dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
                  end-placeholder="结束日期" class="date-picker" />
                <el-switch v-model="form.steadyDelivery" active-text="周期稳投" style="margin-left: 16px;" />
              </el-form-item>

              <div class="subsection-title">定向设置</div>

              <el-form-item label="定向方式" label-width="140px">
                <el-switch v-model="form.isPreset" active-text="预设定向" inactive-text="自定义定向" />
              </el-form-item>

              <el-form-item v-if="form.isPreset" label="预设定向包" label-width="140px">
                <el-select v-model="form.presetValue" value-key="id" filterable placeholder="请选择预设定向包"
                  class="input-medium">
                  <el-option v-for="item in presetOptions" :key="item.id" :label="item.directpkgname" :value="item" />
                </el-select>
              </el-form-item>

              <template v-else>
                <el-form-item label="地域定向" label-width="140px">
                <el-radio-group v-model="form.regionType">
                  <el-radio-button :label="0">不限</el-radio-button>
                  <el-radio-button :label="1">自定义</el-radio-button>
                  <el-radio-button :label="2">地域模板</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <!-- 地域模板 -->
              <el-form-item v-if="form.regionType === 2" label="选择模板" label-width="140px">
                <el-select v-model="form.selectedRegionTemplate" value-key="id" filterable placeholder="请选择地域模板" class="input-medium">
                  <el-option v-for="tpl in regionTemplates" :key="tpl.id" :label="tpl.name" :value="tpl" />
                </el-select>
                <el-button type="text" size="small" @click="applySelectedRegionTemplate" style="margin-left: 8px;">应用</el-button>
                <span v-if="regionTemplates.length === 0" class="form-tip">暂无模板，请先在自定义中保存</span>
              </el-form-item>

              <!-- 自定义地域 -->
              <div v-if="form.regionType === 1">
                <div class="region-template-bar" v-if="regionTemplates.length > 0">
                  <span class="region-template-label">快速应用：</span>
                  <el-button
                    v-for="tpl in regionTemplates"
                    :key="tpl.id"
                    type="text"
                    size="small"
                    @click="applyRegionTemplate(tpl)"
                  >{{ tpl.name }}</el-button>
                </div>
                <div class="region-picker-layout">
                    <div class="region-picker-left">
                      <div class="region-quick-actions">
                        <el-button type="text" size="small" @click="selectAllRegions">全选</el-button>
                        <el-button type="text" size="small" @click="clearRegions" style="color: #f56c6c;">清空</el-button>
                        <el-button type="text" size="small" @click="saveAsRegionTemplate" :disabled="regionSelectedTags.length === 0" style="color: #67c23a;">另存为地域定向</el-button>
                      </div>
                      <!-- 省份列表 -->
                      <div class="region-cascader">
                        <div class="region-left-panel">
                          <div class="region-list">
                            <div
                              v-for="province in regionCascaderOptions"
                              :key="province.value"
                              class="region-row"
                            >
                              <el-checkbox
                                :value="isProvinceChecked(province.value)"
                                :indeterminate="isProvinceIndeterminate(province.value)"
                                @change="val => onProvinceCheck(province, val)"
                              >{{ province.label }}</el-checkbox>
                              <span
                                v-if="province.children.length > 0"
                                class="region-row-btn"
                                @click.stop="showProvinceCities(province)"
                              >{{ currentProvince === province.value ? '‹' : '›' }}</span>
                              <span v-else class="region-row-btn-placeholder"></span>
                            </div>
                          </div>
                        </div>
                        <div class="region-right-panel" v-if="currentProvince && currentProvinceChildren.length > 0">
                          <div class="region-list">
                            <div class="region-select-all-row">
                              <el-checkbox
                                :value="isCurrentProvinceAllChecked"
                                :indeterminate="isCurrentProvinceIndeterminate"
                                @change="toggleCurrentProvinceAll"
                              >全选</el-checkbox>
                            </div>
                            <el-checkbox
                              v-for="city in currentProvinceChildren"
                              :key="city.value"
                              :value="!!form.selectedCities[city.value]"
                              @change="val => onCityToggle(city, val)"
                            >{{ city.label }}</el-checkbox>
                          </div>
                        </div>
                        <div v-else class="region-right-panel region-right-placeholder">
                          <span>{{ currentProvince ? '该省份下暂无城市' : '点击左侧 › 查看城市' }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="region-picker-right">
                      <div class="region-tags-title">已选地域（{{ regionSelectedTags.length }}）</div>
                      <div class="region-tags-list" v-if="regionSelectedTags.length > 0">
                        <span class="region-tag" v-for="tag in regionSelectedTags" :key="tag.id">
                          {{ tag.label }}
                        </span>
                      </div>
                      <div class="region-empty" v-else>未选择</div>
                    </div>
                  </div>
              </div>

                <el-form-item label="性别" label-width="140px">
                  <el-radio-group v-model="form.sex">
                    <el-radio-button label="男">男</el-radio-button>
                    <el-radio-button label="女">女</el-radio-button>
                    <el-radio-button label="不限">不限</el-radio-button>
                  </el-radio-group>
                </el-form-item>

                <el-form-item label="年龄" label-width="140px">
                  <el-checkbox-group v-model="form.age">
                    <el-checkbox-button v-for="item in ages" :key="item.id" :label="item.id">
                      {{ item.value }}
                    </el-checkbox-button>
                  </el-checkbox-group>
                </el-form-item>

                <el-form-item label="安卓版本" label-width="140px">
                  <el-radio-group v-model="form.androidVersionType">
                    <el-radio-button :label="0">不限</el-radio-button>
                    <el-radio-button :label="1">自定义</el-radio-button>
                  </el-radio-group>
                </el-form-item>

                <el-form-item v-if="form.androidVersionType === 1" label="" label-width="140px">
                  <el-checkbox-group v-model="form.selectedVersions">
                    <el-checkbox v-for="v in androidVersions" :key="v.value" :label="v.value">
                      {{ v.label }}
                    </el-checkbox>
                  </el-checkbox-group>
                </el-form-item>

                <el-form-item label="购机价格" label-width="140px">
                  <el-radio-group v-model="form.priceRangeType">
                    <el-radio-button :label="0">不限</el-radio-button>
                    <el-radio-button :label="1">自定义</el-radio-button>
                  </el-radio-group>
                </el-form-item>

                <el-form-item v-if="form.priceRangeType === 1" label="" label-width="140px">
                  <el-checkbox-group v-model="form.selectedPrices">
                    <el-checkbox v-for="p in priceRanges" :key="p.value" :label="p.value">
                      {{ p.label }}
                    </el-checkbox>
                  </el-checkbox-group>
                </el-form-item>

                <el-form-item label="手机系列" label-width="140px">
                  <el-radio-group v-model="form.phoneSeriesStatus">
                    <el-radio-button :label="0">不限</el-radio-button>
                    <el-radio-button :label="1">设置</el-radio-button>
                  </el-radio-group>
                  <div v-if="form.phoneSeriesStatus == 1" class="phone-series-panel">
                    <el-checkbox-group v-model="form.phoneSeriesSSelect">
                      <el-checkbox v-for="item in phoneSerieslist" :key="item.id" :label="item.id">
                        {{ item.name }}
                      </el-checkbox>
                    </el-checkbox-group>
                  </div>
                </el-form-item>
              </template>

              <el-form-item label="安装定向" label-width="140px">
                <el-radio-group v-model="form.installedApp">
                  <el-radio-button label="1">不限</el-radio-button>
                  <el-radio-button label="2">未安装</el-radio-button>
                  <el-radio-button label="3">已安装</el-radio-button>
                </el-radio-group>
              </el-form-item>
            </div>
          </div>

          <!-- ==================== 创意基础信息 ==================== -->
          <div class="section-block" ref="sectionCreative">
            <div class="section-title section-title-primary">
              <i class="el-icon-picture-outline-round section-icon"></i>
              创意基础信息
            </div>
            <div class="section-body">
              <el-form-item label="展现形式" label-width="140px">
                <el-cascader v-model="form.placeType" :options="options4" filterable clearable @change="placeTypeChange"
                  class="input-medium" placeholder="请选择展现形式" />
              </el-form-item>

              <el-form-item label="投放位置" label-width="140px">
                <el-select v-model="form.virtualPositions" multiple collapse-tags clearable filterable placeholder="请选择投放位置"
                  class="input-large">
                  <el-option v-for="item in virtualPositionOptions" :key="item.virtualPositionUuid"
                    :label="item.virtualPositionName" :value="item.virtualPositionUuid" />
                </el-select>
              </el-form-item>

              <el-form-item label="搭建策略" label-width="140px">
                <el-radio-group v-model="form.mixStrategy">
                  <el-radio-button label="重复">重复</el-radio-button>
                  <el-radio-button label="轮询">轮询</el-radio-button>
                </el-radio-group>
              </el-form-item>

              <el-form-item label="标题打乱" label-width="140px">
                <el-switch v-model="form.shuffleTitle" />
                <span class="switch-label">{{ form.shuffleTitle ? '开启' : '关闭' }}</span>
              </el-form-item>

              <el-form-item :label="'产品名（' + form.productNames.length + '个）'" label-width="140px">
                <div class="product-name-list">
                  <div v-for="(p, idx) in form.productNames" :key="idx" class="product-name-item">
                    <el-input v-model="form.productNames[idx]" placeholder="请输入产品名" class="input-short" />
                    <el-button @click="addProduct" type="primary" icon="el-icon-plus" circle
                      v-if="idx === form.productNames.length - 1" />
                    <el-button v-if="form.productNames.length > 1" @click="removeProduct(idx)" type="danger"
                      icon="el-icon-minus" circle />
                  </div>
                </div>
              </el-form-item>

              <el-form-item :label="'标题（' + form.titleList.length + '个）'" label-width="140px">
                <div class="textarea-block">
                  <el-input type="textarea" v-model="titlesString" placeholder="每行一个标题" :rows="8"
                    class="content-textarea" @input="handleTitleInput" />
                  <div v-if="titleErrors.length > 0" class="error-list">
                    <div v-for="(line, index) in titleErrors" :key="index" class="error-item">
                      <i class="el-icon-warning"></i>
                      第{{ index + 1 }}行字符超限(0-35字)：{{ line }}
                    </div>
                  </div>
                </div>
              </el-form-item>

              <el-form-item label="撞审标题" label-width="140px">
                <div class="textarea-block">
                  <el-input type="textarea" v-model="zsTitlesString" placeholder="每行一个撞审标题，作为审核备用" :rows="8"
                    class="content-textarea" @input="handleZsTitleInput" />
                  <div v-if="zsTitleErrors.length > 0" class="error-list">
                    <div v-for="(line, index) in zsTitleErrors" :key="index" class="error-item">
                      <i class="el-icon-warning"></i>
                      第{{ index + 1 }}行字符超限：{{ line }}
                    </div>
                  </div>
                </div>
              </el-form-item>

              <!-- 创意规格（素材） -->
              <div class="subsection-title" ref="sectionCreativeSpec">创意规格</div>

              <el-form-item label="创意素材" label-width="140px">
                <div class="material-area">
                  <el-button type="primary" icon="el-icon-picture" @click="handleMaterialsdialog" class="material-btn">
                    选择素材
                  </el-button>
                  <div class="material-stats" v-if="hasMaterials">
                    <el-tag type="" effect="plain" class="material-tag">
                      <i class="el-icon-picture-outline"></i> LOGO: {{ txfileList.length }}
                    </el-tag>
                    <el-tag type="" effect="plain" class="material-tag">
                      <i class="el-icon-camera"></i> 图片: {{ fileList.length }}
                    </el-tag>
                    <el-tag type="" effect="plain" class="material-tag">
                      <i class="el-icon-film"></i> 封面: {{ videotpfileList.length }}
                    </el-tag>
                    <el-tag type="" effect="plain" class="material-tag">
                      <i class="el-icon-video-camera"></i> 视频: {{ VideofileList.length }}
                    </el-tag>
                  </div>
                  <span class="material-empty" v-else>
                    <i class="el-icon-info"></i> 暂未选择素材，请点击按钮选择
                  </span>
                </div>
              </el-form-item>
            </div>
          </div>
        </el-form>
        </div>
      </div>
    </el-main>

    <!-- 底部操作栏 -->
    <div class="adgroup-footer">
      <div class="footer-content">
        <el-button type="success" icon="el-icon-folder-add" @click="saveTemplate" size="medium">
          保存模板
        </el-button>
        <el-button type="primary" icon="el-icon-s-promotion" @click="startBatchTasks" :loading="isLoading"
          size="medium">
          {{ isLoading ? '处理中...' : '开始搭建' }}
        </el-button>
        <el-button type="info" icon="el-icon-view" @click="openTaskListDialog" v-if="tasks.length > 0" size="medium">
          查看任务进度
        </el-button>
      </div>
    </div>

    <!-- 素材选择对话框 -->
    <el-dialog title="选择创意素材" :visible.sync="creativeMaterials" width="1160px" append-to-body class="material-dialog">
      <div class="m-header" style="height:600px;overflow-y:auto;">
        <el-tabs v-model="activeTab" type="card">
          <el-tab-pane label="品牌LOGO" name="private">
            <div class="tab-placeholder">
              <i class="el-icon-upload"></i>
              <p>品牌LOGO素材区域</p>
            </div>
          </el-tab-pane>
          <el-tab-pane label="图片" name="img">
            <div class="tab-placeholder">
              <i class="el-icon-upload"></i>
              <p>图片素材区域</p>
            </div>
          </el-tab-pane>
          <el-tab-pane label="视频封面" name="imgvideo">
            <div class="tab-placeholder">
              <i class="el-icon-upload"></i>
              <p>视频封面素材区域</p>
            </div>
          </el-tab-pane>
          <el-tab-pane label="视频" name="video">
            <div class="tab-placeholder">
              <i class="el-icon-upload"></i>
              <p>视频素材区域</p>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="creativeMaterials = false">确定</el-button>
        <el-button type="primary" @click="creativeMaterials = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 任务详情弹窗 -->
    <el-dialog title="任务执行列表" :visible.sync="taskListDialogVisible" width="1200px" class="task-dialog">
      <div class="task-list-container">
        <div v-for="task in tasks" :key="task.id" class="task-detail-card">
          <div class="task-detail-header">
            <span class="task-detail-name">{{ task.name }}</span>
            <el-tag :type="getTaskStatusType(task.status)">
              {{ getTaskStatusText(task.status) }}
            </el-tag>
          </div>
          <el-progress :percentage="Math.round(task.currentIndex / task.totalItems * 100)"
            :status="getProgressStatus(task.status)" :stroke-width="6" />
          <div class="task-detail-info">
            已处理: {{ task.currentIndex }} / {{ task.totalItems }}
          </div>
        </div>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import { findListName, getVivoUser, getByIdsName, getDirectpkgsList, getVirtualPositionList, getQualificationList, getCvTypeOptions } from './api.js'
import { regionlist } from './region.js'
import { androidVersions, priceRanges, displayTypeOptions } from './constants.js'

export default {
  data() {
    return {
        // ========== 任务管理 ==========
      showProgress: false,
      uploadPercent: 0,
      isLoading: false,
      tasks: [],
      taskIdCounter: 1,
      taskControllers: new Map(),
      isTaskStopped: new Map(),
      isTaskPaused: new Map(),
      taskListDialogVisible: false,

      // ========== 资质查询 ==========
      qualificationOptions: [],
      qualificationLoading: false,

      // ========== 地域定向 ==========
      currentProvince: null,

      // ========== 投放时段网格 ==========
      timeGrid: {},
      weekDays: ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日'],

      // ========== 地域模板（本地保存，后续改接口） ==========
      regionTemplates: [],
      customColors: [
        { color: '#f56c6c', percentage: 20 },
        { color: '#e6a23c', percentage: 40 },
        { color: '#5cb87a', percentage: 60 },
        { color: '#1989fa', percentage: 80 },
        { color: '#6f7ad3', percentage: 100 }
      ],

      // ========== 步骤条 ==========
      activeStep: 0,
      steps: [
        { name: '广告计划', status: 'wait', detail: '' },
        { name: '广告组', status: 'wait', detail: '' },
        { name: '广告创意', status: 'wait', detail: '' }
      ],

      // ========== 账户信息 ==========
      listname: [],
      two: [],
      btgjc: "",
      towName: "",
      ids_list: [],
      selectedRegion: "",
      regionOptions: [
        { label: "浙江", value: "浙江" },
        { label: "福建", value: "福建" },
        { label: "深圳", value: "深圳" },
        { label: "苏州", value: "苏州" },
        { label: "广州力天", value: "广州力天" },

      ],
      username: "",
      idsList: [],
      erroridslist: [],

      // ========== VIVO 表单配置 ==========
      form: {
        extension_type: 1,
        mediaType: '1',
        dailyBudget: '',
        planSwitch: false,
        latestPlan: false,
        wechatFollow: false,
        adgroupNumber: 1,
        qualification: [],
        cvType: null,
        ocpxPrice: '',
        useOldPrice: false,
        audienceIds: '',
        excludeAudienceIds: '',
        timeRanges: [{ start: '00:00', end: '24:00' }],
        timeSettingType: 0,
        dateRange: [],
        steadyDelivery: false,
        isPreset: false,
        presetValue: null,
        selectedRegions: [],
        selectedCities: {},
        regionType: 0,
        selectedRegionTemplate: null,
        sex: '不限',
        age: [],
        selectedVersions: [],
        androidVersionType: 0,
        selectedPrices: [],
        priceRangeType: 0,
        phoneSeriesStatus: 0,
        phoneSeriesSSelect: [],
        installedApp: '1',
        placeType: [],
        virtualPositions: [],
        mixStrategy: '轮询',
        shuffleTitle: true,
        productNames: [''],
        titleList: [],
        zsTitleList: [],
        campaignType: 0,
        promotionType: 0,
        conversionMonitorType: 0,
        budgetType: 0,
        planName: ''
      },

      // ========== VIVO 选项数据 ==========
      extensionType_goal_group: [
        { value: 1, label: "网址推广" },
        { value: 2, label: "应用推广" },
        { value: 8, label: "快应用推广" },
        { value: 11, label: "小程序推广" }
      ],
      cvOptions: [],
      mapArray: [
       ],
      ages: [
        { id: 25, value: '0-17岁' },
        { id: 26, value: '18-24岁' },
        { id: 27, value: '25-29岁' },
        { id: 28, value: '30-39岁' },
        { id: 29, value: '40-49岁' },
        { id: 30, value: '50岁及以上' }
      ],
      androidVersions: androidVersions,
      priceRanges: priceRanges,
      phoneSerieslist: [
        { id: 1530002, name: "i系列" },
        { id: 1530003, name: "X系列" },
        { id: 1530004, name: "S系列" },
        { id: 1530005, name: "Y系列" },
        { id: 1530006, name: "Z系列" },
        { id: 1530007, name: "NEX系列" },
      ],
      options4: displayTypeOptions,
      virtualPositionOptions: [],
      presetOptions: [],

      // ========== 素材管理 ==========
      creativeMaterials: false,
      activeTab: 'private',
      txfileList: [],
      txfileCode: [],
      fileList: [],
      fileCode: [],
      videotpfileList: [],
      videotpfileCode: [],
      VideofileList: [],
      videofileCode: [],
      uploadingMaterials: [],

      // ========== 文案 ==========
      titlesString: '',
      zsTitlesString: '',
      titleErrors: [],
      zsTitleErrors: [],

      // ========== 其他 ==========
      pickerOptions: { start: '00:00', step: '00:30', end: '24:00' },

      // ========== 快捷导航 ==========
      navItems: [
        { ref: 'sectionAccount', label: '账户信息', icon: 'el-icon-user-solid', active: false },
        { ref: 'sectionPlan', label: '广告计划', icon: 'el-icon-s-flag', active: false },
        { ref: 'sectionGroup', label: '广告组', icon: 'el-icon-s-grid', active: false },
        { ref: 'sectionCreative', label: '创意基础信息', icon: 'el-icon-picture-outline-round', active: false },
        { ref: 'sectionCreativeSpec', label: '创意规格', icon: 'el-icon-picture-outline', active: false }
      ]
    };
  },

  computed: {
    hasMaterials() {
      return this.txfileList.length > 0 || this.fileList.length > 0 ||
        this.videotpfileList.length > 0 || this.VideofileList.length > 0;
    },
    // 根据推广目标动态返回可用的投放渠道
    // 推广目标: 1=网址推广, 2=应用推广, 8=快应用推广, 11=小程序推广
    availableMediaTypes() {
      const mediaTypeMap = {
        1: [  // 普通网址
          { value: '1', label: '非应用商店' },
          { value: '2', label: '广告联盟' }
        ],
        2: [  // 应用推广
          { value: '0', label: '商店' },
          { value: '1', label: '非应用商店' },
          { value: '2', label: '广告联盟' }
        ],
        8: [  // 快应用/快游戏
          { value: '0', label: '商店' },
          { value: '1', label: '非应用商店' },
          { value: '2', label: '广告联盟' }
        ],
        11: [  // 小程序
          { value: '1', label: '非应用商店' },
          { value: '2', label: '广告联盟' }
        ]
      };
      return mediaTypeMap[this.form.extension_type] || [];
    },
    // 是否显示场景定制（promotionType）
    // 应用推广 + 商店：普通投放+智投；应用推广 + 非商店/联盟：仅普通投放
    showPromotionType() {
      return this.form.extension_type === 2;
    },
    // 场景定制中是否有智投选项（仅应用推广+商店时）
    promotionTypeHasZhito() {
      return this.form.extension_type === 2 && this.form.mediaType === '0';
    },
    // 是否显示广告类型（campaignType）
    // 应用推广+非商店、网址推广+非商店、快应用+非商店 时显示
    showCampaignType() {
      const adType = this.form.extension_type;
      const media = this.form.mediaType;
      // 应用推广 + 非商店
      if (adType === 2 && media === '1') return true;
      // 网址推广 + 非商店
      if (adType === 1 && media === '1') return true;
      // 快应用 + 非商店
      if (adType === 8 && media === '1') return true;
      return false;
    },
    // 是否显示转化监测（conversionMonitorType）
    // 仅应用推广时显示
    showConversionMonitorType() {
      return this.form.extension_type === 2;
    },
    // 用于监听转化目标查询条件变化（合并多个依赖，避免重复触发）
    cvTypeQueryKey() {
      return `${this.form.extension_type}_${this.form.mediaType}_${this.form.campaignType}_${this.ids_list.length ? this.ids_list[0].ids : ''}`;
    },
    // ========== 地域定向 ==========
    // 转换为级联选择器需要的树形结构
    regionCascaderOptions() {
      return regionlist.map(province => {
        const children = (province.childBaseTag || []).map(city => ({
          value: city.id,
          label: city.tagName
        }));
        return {
          value: province.id,
          label: province.tagName,
          children: children.length > 0 ? children : []
        };
      });
    },
    // 当前选中省份下的城市
    currentProvinceChildren() {
      if (!this.currentProvince) return [];
      // 支持 currentProvince 是省份对象或省份ID
      const provinceId = typeof this.currentProvince === 'object'
        ? this.currentProvince.value
        : this.currentProvince;
      const p = regionlist.find(x => x.id === provinceId);
      if (!p) return [];
      return (p.childBaseTag || []).map(c => ({ value: c.id, label: c.tagName }));
    },
    // 已选地域标签（用于右侧展示）
    regionSelectedTags() {
      const tags = [];
      const idMap = {};
      const childrenMap = {};
      regionlist.forEach(p => {
        idMap[p.id] = p.tagName;
        childrenMap[p.id] = (p.childBaseTag || []).map(c => c.id);
        (p.childBaseTag || []).forEach(c => { idMap[c.id] = c.tagName; });
      });
      // 优先显示完整省份
      (this.form.selectedRegions || []).forEach(provinceId => {
        const cityCount = (childrenMap[provinceId] || []).length;
        const label = cityCount > 0
          ? `${idMap[provinceId] || ''} (含${cityCount}城市)`
          : idMap[provinceId] || '';
        tags.push({ id: provinceId, label, isProvince: true });
      });
      // 再显示零散城市（只显示父级未被勾、且值为 true 的）
      const selectedProvinceSet = new Set(this.form.selectedRegions || []);
      Object.keys(this.form.selectedCities || {}).forEach(cityIdStr => {
        if (!this.form.selectedCities[cityIdStr]) return; // 只显示明确勾选的城市
        const cityId = parseInt(cityIdStr);
        // 找出父级
        let parentId = null;
        for (const p of regionlist) {
          if ((p.childBaseTag || []).some(c => c.id === cityId)) {
            parentId = p.id;
            break;
          }
        }
        if (parentId && !selectedProvinceSet.has(parentId)) {
          tags.push({ id: cityId, label: idMap[cityId] || '', isCity: true });
        }
      });
      return tags;
    },
    // 智能传值：子级全选只传父级ID，部分选传子级ID
    regionIdList() {
      const provinces = this.form.selectedRegions || [];
      const cities = this.form.selectedCities || {};
      if (provinces.length === 0 && Object.keys(cities).length === 0) return [];

      const result = [];
      // 完整省份
      provinces.forEach(pid => result.push(pid));
      // 城市：排除父级已勾的
      const provinceSet = new Set(provinces);
      Object.keys(cities).forEach(cidStr => {
        const cid = parseInt(cidStr);
        // 找父级
        let parentId = null;
        for (const p of regionlist) {
          if ((p.childBaseTag || []).some(c => c.id === cid)) {
            parentId = p.id;
            break;
          }
        }
        if (parentId && !provinceSet.has(parentId)) {
          result.push(cid);
        }
      });
      return result;
    },
    // ========== 投放时段 ==========
    hasSelectedTimes() {
      return Object.keys(this.timeGrid).length > 0;
    },
    timeSummary() {
      const result = [];
      for (let day = 0; day < 7; day++) {
        const hours = [];
        for (let h = 1; h <= 24; h++) {
          if (this.timeGrid[`${day}_${h}`]) hours.push(h - 1);
        }
        if (hours.length === 0) continue;
        hours.sort((a, b) => a - b);
        const ranges = [];
        let start = hours[0];
        let end = start;
        for (let i = 1; i <= hours.length; i++) {
          if (i < hours.length && hours[i] === end + 1) {
            end = hours[i];
          } else {
            ranges.push(`${String(start).padStart(2, '0')}:00-${String(end + 1).padStart(2, '0')}:00`);
            if (i < hours.length) { start = hours[i]; end = start; }
          }
        }
        result.push({ weekday: this.weekDays[day], hours: ranges.join(', ') });
      }
      return result;
    },
    timeGridData() {
      const arr = [];
      for (let day = 0; day < 7; day++) {
        let s = '';
        for (let h = 0; h < 24; h++) {
          s += this.timeGrid[`${day}_${h + 1}`] ? '1' : '0';
        }
        arr.push(s);
      }
      return arr;
    }
  },

  watch: {
    cvTypeQueryKey() {
      this.fetchCvTypeOptions();
    }
  },

  created() {
    this.loadAccountList();
    this.fetchPresetOptions();
  },

  methods: {
    // ========== 账户相关 ==========
    loadAccountList() {
      findListName().then(res => {
        this.listname = res.data;
      });
    },
    recordsChange() {
      this.GetRo();
    },
    async GetRo() {
      if (!this.btgjc) return;
      const res = await getVivoUser(this.btgjc);
      this.two = res.data;
    },
    async GetRoChange() {
      await this.findIds();
    },
    handleRegionChange() {
      // 区域变更时的处理
    },
    async findIds() {
      let list = this.username.split('\n').filter(line => line.trim());
      if (list.length === 0) {
        this.$message.warning('请输入账户名');
        return;
      }
      this.ids_list = [];

      try {
        const responses = await Promise.all(
          list.map(item => getByIdsName(item))
        );

        const allData = responses.flatMap(res => res.data);
        this.ids_list = this.deduplicateById(allData);
        this.$message.success(`查询成功，共获取 ${this.ids_list.length} 个账户`);
        // 自动查询资质
        this.queryQualification();
      } catch (error) {
        console.error("请求失败:", error);
        this.$message.error('查询失败，请重试');
      }
    },
    deduplicateById(arr) {
      const seen = new Set();
      return arr.filter(item => {
        if (seen.has(item.ids)) {
          return false;
        }
        seen.add(item.ids);
        return true;
      });
    },
    removeAccount(index) {
      this.ids_list.splice(index, 1);
    },

    // ========== 地域定向辅助 ==========
    getProvinceAllChildren(provinceId) {
      const province = regionlist.find(p => p.id === provinceId);
      if (!province || !province.childBaseTag) return [];
      return province.childBaseTag.map(c => c.id);
    },
    isProvinceChecked(provinceId) {
      return (this.form.selectedRegions || []).includes(provinceId);
    },
    isProvinceIndeterminate(provinceId) {
      if (this.isProvinceChecked(provinceId)) return false;
      const children = this.getProvinceAllChildren(provinceId);
      if (children.length === 0) return false;
      // 只要有任一子级被勾上
      return children.some(cid => this.form.selectedCities[cid]);
    },
    onProvinceCheck(province, checked) {
      const provinces = [...(this.form.selectedRegions || [])];
      if (checked) {
        if (!provinces.includes(province.value)) {
          provinces.push(province.value);
        }
        // 勾选省份时，把该省下所有城市也标记为 true（视觉全选，传值只传省）
        const children = province.children || [];
        children.forEach(c => {
          this.$set(this.form.selectedCities, c.value, true);
        });
        this.currentProvince = province.value;
      } else {
        const idx = provinces.indexOf(province.value);
        if (idx >= 0) provinces.splice(idx, 1);
        // 取消省份时，把该省下所有城市也取消
        const children = province.children || [];
        children.forEach(c => {
          this.$set(this.form.selectedCities, c.value, false);
        });
      }
      this.form.selectedRegions = provinces;
    },
    showProvinceCities(province) {
      // 强制重新设置 currentProvince，保证响应式更新
      const newVal = this.currentProvince === province.value ? null : province.value;
      this.currentProvince = newVal;
    },
    // 当前省份是否全选
    isCurrentProvinceAllChecked() {
      const children = this.currentProvinceChildren;
      if (children.length === 0) return false;
      return children.every(c => this.form.selectedCities[c.value] === true);
    },
    // 当前省份是否半选
    isCurrentProvinceIndeterminate() {
      const children = this.currentProvinceChildren;
      if (children.length === 0) return false;
      const checkedCount = children.filter(c => this.form.selectedCities[c.value] === true).length;
      return checkedCount > 0 && checkedCount < children.length;
    },
    // 全选/取消全选当前省份的城市
    toggleCurrentProvinceAll(checked) {
      const children = this.currentProvinceChildren;
      if (checked) {
        children.forEach(c => {
          this.$set(this.form.selectedCities, c.value, true);
        });
      } else {
        children.forEach(c => {
          this.$set(this.form.selectedCities, c.value, false);
        });
      }
    },
    // 单个城市勾选/取消
    onCityToggle(city, checked) {
      this.$set(this.form.selectedCities, city.value, checked);
      const children = this.currentProvinceChildren;
      const allChecked = children.every(c => this.form.selectedCities[c.value] === true);
      if (allChecked) {
        // 全选 → 自动加入省份，但城市保持勾选状态（视觉一致，传值由 regionIdList 处理）
        if (!this.form.selectedRegions.includes(this.currentProvince)) {
          this.form.selectedRegions.push(this.currentProvince);
        }
      } else {
        // 没全选 → 移除省份
        const idx = this.form.selectedRegions.indexOf(this.currentProvince);
        if (idx >= 0) {
          this.form.selectedRegions.splice(idx, 1);
        }
      }
    },
    handleRegionCheckChange() {
      // 由新方法处理
    },
    selectAllRegions() {
      this.form.selectedRegions = regionlist.map(p => p.id);
      this.form.selectedCities = {};
    },
    clearRegions() {
      this.form.selectedRegions = [];
      this.form.selectedCities = {};
    },
    saveAsRegionTemplate() {
      const regionIds = this.regionIdList;
      if (regionIds.length === 0) {
        this.$message.warning('请先选择地域');
        return;
      }
      const tagNames = this.regionSelectedTags.map(t => t.label).join('、');
      const name = tagNames.length > 20 ? tagNames.slice(0, 20) + '...' : tagNames;
      const template = {
        id: Date.now(),
        name: name,
        regionIds: [...regionIds]
      };
      // 避免重复
      const exists = this.regionTemplates.find(t => t.name === template.name);
      if (!exists) {
        this.regionTemplates.unshift(template);
      }
      this.$message.success(`地域模板「${name}」已保存`);
    },
    applyRegionTemplate(tpl) {
      // 根据保存的 regionIds 反推 selectedRegions + selectedCities
      const provinces = [];
      const cities = {};
      const { regionIds } = tpl;
      regionlist.forEach(province => {
        const children = province.childBaseTag || [];
        if (regionIds.includes(province.id)) {
          // 整个省
          provinces.push(province.id);
          children.forEach(c => { cities[c.id] = false; });
        } else {
          children.forEach(c => {
            if (regionIds.includes(c.id)) {
              cities[c.id] = true;
            }
          });
        }
      });
      this.form.selectedRegions = provinces;
      this.form.selectedCities = cities;
      this.$message.success(`已应用地域模板「${tpl.name}」`);
    },
    applySelectedRegionTemplate() {
      if (!this.form.selectedRegionTemplate) {
        this.$message.warning('请先选择模板');
        return;
      }
      this.applyRegionTemplate(this.form.selectedRegionTemplate);
    },

    // ========== 资质查询 ==========
    async queryQualification() {
      if (!this.ids_list.length) {
        this.$message.warning('请先查询账户');
        return;
      }
      const { ids, token } = this.ids_list[0];
      const timestamp = parseInt(new Date().getTime()) + '';
      const nonce = Math.round(Math.random() * 100000000000);

      this.qualificationLoading = true;
      this.qualificationOptions = [];
      try {
        const res = await getQualificationList({ token, uid: ids, timestamp, nonce });
        if (res.data.code === 0 && res.data.data) {
          this.qualificationOptions = this.transformQualificationData(res.data.data);
          this.$message.success('资质查询成功');
        } else {
          this.$message.warning(res.data.message || '未查询到资质数据');
        }
      } catch (error) {
        console.error('资质查询失败:', error);
        this.$message.error('资质查询失败');
      } finally {
        this.qualificationLoading = false;
      }
    },
    transformQualificationData(data) {
      // data: [{ industry1, industry2, industry1Name, industry2Name, advertiseQualificationList: [{ advertiseQualificationId, appCnName }] }]
      const industry1Map = {};
      data.forEach(item => {
        const key1 = item.industry1;
        if (!industry1Map[key1]) {
          industry1Map[key1] = {
            value: item.industry1,
            label: item.industry1Name,
            children: {},
            childrenList: []
          };
        }
        const key2 = item.industry2;
        if (!industry1Map[key1].children[key2]) {
          industry1Map[key1].children[key2] = {
            value: item.industry2,
            label: item.industry2Name,
            children: []
          };
        }
        (item.advertiseQualificationList || []).forEach(q => {
          industry1Map[key1].children[key2].children.push({
            value: q.advertiseQualificationId,
            label: q.appCnName
          });
        });
      });
      return Object.values(industry1Map).map(g1 => ({
        value: g1.value,
        label: g1.label,
        children: Object.values(g1.children)
      }));
    },

    // ========== 转化目标查询 ==========
    async fetchCvTypeOptions() {
      if (!this.ids_list.length) return;
      const { ids, token } = this.ids_list[0];
      const timestamp = parseInt(new Date().getTime()) + '';
      const nonce = Math.round(Math.random() * 100000000000);

      try {
        const res = await getCvTypeOptions({
          token,
          uid: ids,
          timestamp,
          nonce,
          adType: this.form.extension_type,
          mediaType: this.form.mediaType,
          campaignType: this.form.campaignType
        });
        if (res.data.code === 0 && res.data.data) {
          const list = res.data.data.ocpxCvTypeVoList || [];
          this.cvOptions = list.map(item => ({
            value: item.cvTypeDetail.cvType,
            label: item.cvTypeDetail.cvTypeName
          }));
        } else {
          this.cvOptions = [];
        }
      } catch (error) {
        console.error('转化目标查询失败:', error);
        this.cvOptions = [];
      }
    },

    // ========== 预设定向包 ==========
    fetchPresetOptions() {
      getDirectpkgsList().then(res => {
        this.presetOptions = res.data;
      });
    },

    // ========== 展现形式变化 ==========
    async placeTypeChange(val) {
      if (!this.form.mediaType) {
        this.$message.warning('请选择投放渠道后重选');
        return;
      }
      if (!this.form.extension_type) {
        this.$message.warning('请选择推广目标后重选');
        return;
      }
      if (!this.ids_list.length) {
        this.$message.warning('请先查询账户');
        return;
      }
      const [displayType, normId] = val;
      const { token, ids } = this.ids_list[0];
      const timestamp = parseInt(new Date().getTime()) + '';
      const nonce = Math.round(Math.random() * 100000000000);
      try {
        const res = await getVirtualPositionList({
          token,
          ids,
          timestamp,
          nonce,
          normId: parseInt(normId) || 0,
          mediaType: parseInt(this.form.mediaType) || 0,
          adType: parseInt(this.form.extension_type) || 0,
          displayType: parseInt(displayType) || 0
        });
        this.virtualPositionOptions = res.data.data || [];
        this.form.virtualPositions = this.virtualPositionOptions.map(item => item.virtualPositionUuid);
      } catch (e) {
        console.error(e);
        this.virtualPositionOptions = [];
        this.form.virtualPositions = [];
      }
    },
    extensionTypeChange() {
      // 推广目标变更时，自动切换到当前可用的第一个投放渠道
      const available = this.availableMediaTypes;
      if (available.length > 0) {
        this.form.mediaType = available[0].value;
      }
    },

    // ========== 时段操作 ==========
    addRange() {
      const last = this.form.timeRanges[this.form.timeRanges.length - 1];
      this.form.timeRanges.push({ start: last.end, end: '24:00' });
    },
    removeRange(idx) {
      if (this.form.timeRanges.length > 1) {
        this.form.timeRanges.splice(idx, 1);
      }
    },
    // 网格拖拽选择
    startDragSelect(day, hour, event) {
      this._isDragging = true;
      this._dragStartDay = day;
      this._dragStartHour = hour;
      const key = `${day}_${hour}`;
      this._dragState = !this.timeGrid[key];
      this.$set(this.timeGrid, key, this._dragState);
      event.preventDefault();
    },
    onDragEnter(day, hour) {
      if (!this._isDragging) return;
      const minDay = Math.min(this._dragStartDay, day);
      const maxDay = Math.max(this._dragStartDay, day);
      const minHour = Math.min(this._dragStartHour, hour);
      const maxHour = Math.max(this._dragStartHour, hour);
      for (let d = minDay; d <= maxDay; d++) {
        for (let h = minHour; h <= maxHour; h++) {
          this.$set(this.timeGrid, `${d}_${h}`, this._dragState);
        }
      }
    },
    endDragSelect() {
      this._isDragging = false;
      this._dragStartDay = null;
      this._dragStartHour = null;
    },
    clearTimeGrid() {
      Object.keys(this.timeGrid).forEach(key => {
        this.$set(this.timeGrid, key, false);
      });
      this.timeGrid = {};
    },

    // ========== 产品名操作 ==========
    addProduct() {
      this.form.productNames.push('');
    },
    removeProduct(idx) {
      if (this.form.productNames.length > 1) {
        this.form.productNames.splice(idx, 1);
      }
    },

    // ========== 文案处理 ==========
    handleTitleInput() {
      const lines = this.titlesString.split('\n').filter(l => l.trim());
      this.form.titleList = lines;
      const maxLen = 35;
      this.titleErrors = lines.filter(l => l.length > maxLen);
    },
    handleZsTitleInput() {
      const lines = this.zsTitlesString.split('\n').filter(l => l.trim());
      this.form.zsTitleList = lines;
      const maxLen = 35;
      this.zsTitleErrors = lines.filter(l => l.length > maxLen);
    },

    // ========== 素材对话框 ==========
    handleMaterialsdialog() {
      this.creativeMaterials = true;
    },

    // ========== 任务管理核心逻辑 ==========
    startBatchTasks() {
      if (this.ids_list.length === 0) {
        this.$message.warning('请先选择账户');
        return;
      }
      const total = this.ids_list.length;
      const id = this.taskIdCounter++;
      const newTask = {
        id: id,
        name: `批量任务 #${id} - ${total}个账户`,
        status: 'running',
        createdAt: new Date().toLocaleString(),
        currentIndex: 0,
        totalItems: total,
        userDetails: this.ids_list.map(user => ({
          accountName: user.idsname,
          status: 'waiting',
          steps: [
            { name: '上传素材', status: 'wait', detail: '' },
            { name: '创建计划', status: 'wait', detail: '' },
            { name: '创建广告组', status: 'wait', detail: '' },
            { name: '创建创意', status: 'wait', detail: '' },
            { name: '完成', status: 'wait', detail: '' }
          ]
        })),
        steps: [
          { name: '批量处理', status: 'wait', detail: '' },
          { name: '完成', status: 'wait', detail: '' }
        ]
      };
      this.tasks.unshift(newTask);
      this.showProgress = true;
      this.executeBatchTask(id);
    },

    async executeBatchTask(taskId) {
      const task = this.tasks.find(t => t.id === taskId);
      if (!task) return;
      for (let i = 0; i < task.totalItems; i++) {
        await this.delay(1000);
        task.currentIndex = i + 1;
        this.uploadPercent = Math.round((task.currentIndex / task.totalItems) * 100);
        const user = task.userDetails[i];
        if (user) {
          user.status = 'done';
          user.steps.forEach(step => { step.status = 'success'; });
        }
        if (this.isTaskStopped.get(taskId)) break;
        while (this.isTaskPaused.get(taskId)) {
          await this.delay(500);
        }
      }
      task.status = 'done';
      if (this.uploadPercent === 100) {
        this.showProgress = false;
        this.$notify.success({
          title: '任务完成',
          message: '所有账户已处理完毕',
          duration: 3000
        });
      }
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    stopTask(taskId) {
      this.isTaskStopped.set(taskId, true);
      const task = this.tasks.find(t => t.id === taskId);
      if (task) task.status = 'stopped';
      this.$message.warning(`任务已停止`);
    },
    pauseTask(taskId) {
      this.isTaskPaused.set(taskId, true);
      const task = this.tasks.find(t => t.id === taskId);
      if (task) task.status = 'paused';
      this.$message.info(`任务已暂停`);
    },
    resumeTask(taskId) {
      this.isTaskPaused.set(taskId, false);
      const task = this.tasks.find(t => t.id === taskId);
      if (task) task.status = 'running';
      this.$message.success(`任务已继续`);
    },
    clearCompletedTasks() {
      this.tasks = this.tasks.filter(t => t.status === 'running' || t.status === 'paused');
    },
    openTaskListDialog() {
      this.taskListDialogVisible = true;
    },

    // ========== 步骤条状态 ==========
    getStepStatus(status) {
      const map = { wait: 'wait', doing: 'process', success: 'success', error: 'error' };
      return map[status] || 'wait';
    },
    getTaskStatusType(status) {
      const map = {
        waiting: 'info',
        running: 'warning',
        paused: 'info',
        done: 'success',
        failed: 'danger',
        stopped: 'info',
        error: 'danger'
      };
      return map[status] || 'info';
    },
    getTaskStatusText(status) {
      const map = {
        waiting: '等待中',
        running: '运行中',
        paused: '已暂停',
        done: '已完成',
        failed: '失败',
        stopped: '已停止',
        error: '错误'
      };
      return map[status] || status;
    },
    getProgressStatus(status) {
      if (status === 'done') return 'success';
      if (status === 'failed' || status === 'error') return 'exception';
      return null;
    },

    // ========== 快捷导航 ==========
    scrollToSection(refName) {
      // 高亮当前项
      this.navItems.forEach(item => {
        item.active = (item.ref === refName);
      });
      // 滚动到对应区域
      this.$nextTick(() => {
        const el = this.$refs[refName];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    handleScroll() {
      // 根据滚动位置自动高亮导航项
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      let currentRef = '';
      const navRefs = this.navItems.map(item => item.ref);
      
      for (let i = navRefs.length - 1; i >= 0; i--) {
        const el = this.$refs[navRefs[i]];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200) {
            currentRef = navRefs[i];
            break;
          }
        }
      }
      
      this.navItems.forEach(item => {
        item.active = (item.ref === currentRef);
      });
    },

    // ========== 模板保存 ==========
    saveTemplate() {
      this.$message.info('保存模板功能开发中，敬请期待');
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style scoped>
/* ==================== 全局容器 ==================== */
.vivopda-container {
  min-height: 100vh;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", "Microsoft YaHei", sans-serif;
}

/* ==================== 页面头部 ==================== */
.page-header {
  height: 72px !important;
  background: linear-gradient(135deg, #4158D0, #6C63FF, #4A90E2);
  box-shadow: 0 2px 12px rgba(65, 88, 208, 0.3);
  padding: 0 32px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
}

.header-title {
  text-align: center;
}

.header-title h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 1px;
}

.header-version {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.5px;
}

.header-right {
  display: flex;
  align-items: center;
}

/* ==================== 步骤条 ==================== */
.ad-flow {
  position: fixed;
  top: 140px;
  right: 40px;
  width: 220px;
  padding: 24px 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
}

.ad-flow:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.ad-flow-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.ad-flow-header i {
  color: #6C63FF;
  font-size: 18px;
  animation: rotate 1.5s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.progress-percent {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}

/* 过渡动画 */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.6, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(40px);
  opacity: 0;
}

/* ==================== 页面布局 ==================== */
.page-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

/* ==================== 左侧快捷导航 ==================== */
.quick-nav {
  width: 180px;
  flex-shrink: 0;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  padding: 16px 0;
  position: fixed;
  top: 188px;
  left: max(32px, calc((100vw - 1400px) / 2 + 32px));
  transition: box-shadow 0.3s ease;
  z-index: 100;
}

.nav-placeholder {
  width: 180px;
  flex-shrink: 0;
  visibility: hidden;
  pointer-events: none;
}

.nav-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding: 0 16px 12px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.nav-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 8px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  color: #606266;
}

.nav-item:hover {
  background: #f0f2f5;
  color: #4158D0;
}

.nav-item.active {
  background: linear-gradient(135deg, #eef0ff, #e8eaff);
  color: #4158D0;
  font-weight: 600;
}

.nav-item i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

/* ==================== 主体区域 ==================== */
.page-main {
  padding: 28px 32px;
  max-width: 1400px;
  margin: 0 auto;
}

.plan-container {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  padding: 28px 32px;
  margin-bottom: 100px;
}

/* ==================== 任务卡片 ==================== */
.task-card {
  margin-bottom: 28px;
  border-radius: 12px;
  border: 1px solid #e8eaed;
  overflow: hidden;
}

.task-card>>>.el-card__header {
  background: #fafbfc;
  border-bottom: 1px solid #e8eaed;
  padding: 14px 20px;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.task-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.task-header-left i {
  color: #4158D0;
  font-size: 18px;
}

.task-header-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.task-badge {
  margin-left: 4px;
}

.task-header-actions .el-button {
  font-size: 12px;
  color: #6C63FF;
}

.task-list {
  max-height: 320px;
  overflow-y: auto;
}

.task-item {
  margin-bottom: 14px;
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fafcfd;
  transition: all 0.2s ease;
}

.task-item:hover {
  border-color: #d0d5ff;
  background: #f8f9ff;
  box-shadow: 0 2px 8px rgba(108, 99, 255, 0.08);
}

.task-item-icon {
  color: #909399;
  margin-right: 6px;
}

.task-name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
}

.status-tag {
  margin-left: 10px;
}

.task-actions {
  text-align: right;
}

.task-progress {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.task-progress-label {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  min-width: 100px;
}

.task-progress .el-progress {
  flex: 1;
}

/* 列表过渡 */
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ==================== 分区区块 ==================== */
.section-block {
  margin-bottom: 8px;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.2s ease;
}

.section-block:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.section-title-primary {
  background: linear-gradient(135deg, #f5f7ff, #eef0ff);
  color: #4158D0;
  border-bottom: 1px solid #e0e3ff;
}

.section-icon {
  font-size: 20px;
  opacity: 0.8;
}

.section-body {
  padding: 24px 28px 8px;
}

.subsection-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  padding: 10px 0 10px 12px;
  margin: 8px 0 16px;
  border-left: 3px solid #6C63FF;
  background: #f8f9fc;
  border-radius: 0 4px 4px 0;
  letter-spacing: 0.5px;
}

/* ==================== 表单样式 ==================== */
.vivopda-form {
  max-width: 100%;
}

.vivopda-form>>>.el-form-item {
  margin-bottom: 22px;
}

.vivopda-form>>>.el-form-item__label {
  color: #606266;
  font-weight: 500;
  font-size: 14px;
}

.switch-label {
  margin-left: 10px;
  font-size: 13px;
  color: #909399;
}

.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #c0c4cc;
}

.input-suffix {
  margin-left: 6px;
  font-size: 13px;
  color: #606266;
}

/* 输入框尺寸 */
.input-mini {
  width: 120px;
}

.input-short {
  width: 220px;
}

.input-medium {
  width: 320px;
}

.input-large {
  width: 460px;
}

/* ==================== 账户输入区 ==================== */
.account-input-area {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.account-textarea {
  width: 440px;
}

.region-select {
  width: 180px;
}

/* ==================== 地域选择器布局 ==================== */
.region-picker-layout {
  display: flex;
  gap: 16px;
  width: 100%;
}
.region-picker-left {
  flex: 1;
  min-width: 320px;
  max-width: 500px;
}
.region-quick-actions {
  margin-bottom: 6px;
  display: flex;
  gap: 8px;
}
.region-template-bar {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.region-template-label {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}
.region-cascader {
  display: flex;
  width: 100%;
  height: 320px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  overflow: hidden;
}
.region-left-panel,
.region-right-panel {
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  border-right: 1px solid #ebeef5;
}
.region-right-panel {
  border-right: none;
}
.region-right-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;
}
.region-list {
  padding: 6px 0;
}
.region-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
}
.region-list .el-checkbox {
  display: flex;
  width: 100%;
  padding: 6px 12px;
  margin-right: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.region-left-panel .region-list .el-checkbox {
  padding-left: 24px;
}
.region-list .el-checkbox>>>label {
  width: 100%;
}
.region-row-count {
  font-size: 11px;
  color: #909399;
}
.region-row-btn {
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  user-select: none;
  line-height: 1;
}
.region-row-btn:hover {
  background: #ecf5ff;
  border-radius: 4px;
}
.region-row-btn-placeholder {
  width: 28px;
  flex-shrink: 0;
}
.region-select-all-row {
  padding: 6px 12px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 4px;
}
.region-select-all-row .el-checkbox {
  font-weight: 600;
  color: #409eff;
}
.region-picker-right {
  flex: 0 0 220px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
  background: #fafbfc;
  max-height: 360px;
  overflow-y: auto;
}
.region-tags-title {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 10px;
}
.region-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.region-tag {
  display: inline-block;
  padding: 2px 10px;
  font-size: 12px;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #d9ecff;
  border-radius: 4px;
}
.region-empty {
  font-size: 12px;
  color: #c0c4cc;
  text-align: center;
  padding: 20px 0;
}

.query-btn {
  height: 40px;
  padding: 0 24px;
  font-size: 14px;
}

.account-tags {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.account-tags .el-tag {
  border-radius: 6px;
  font-size: 12px;
}

/* ==================== 时段选择 ==================== */
.time-range-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.time-range-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.time-picker {
  width: 160px;
}
.time-separator {
  color: #909399;
  font-size: 13px;
}
.time-action-btn {
  font-size: 18px;
  color: #6C63FF;
}
.time-action-btn.danger {
  color: #f56c6c;
}
.date-picker {
  width: 360px;
}

/* ==================== 时段网格 ==================== */
.time-grid-form-item {
  margin-left: 140px;
  margin-bottom: 22px;
}
.time-grid-wrapper {
  border: 1px solid #e8eef2;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
}
.grid-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8eef2;
}
.grid-tip {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 6px;
}
.grid-container {
  overflow-x: auto;
  padding: 12px;
}
.grid-table {
  border-collapse: collapse;
  width: 100%;
  min-width: 800px;
}
.grid-table th,
.grid-table td {
  border: 1px solid #e8eef2;
  text-align: center;
  font-size: 12px;
}
.weekday-header {
  background: #f8f9fa;
  padding: 10px 8px;
  font-weight: 500;
  color: #606266;
}
.hour-header {
  background: #f8f9fa;
  padding: 6px 2px;
  font-weight: 500;
  color: #909399;
  min-width: 28px;
  font-size: 11px;
}
.weekday-cell {
  background: #fafbfc;
  font-weight: 500;
  color: #606266;
  padding: 8px;
}
.time-cell {
  width: 28px;
  height: 24px;
  cursor: pointer;
  background-color: #fff;
  transition: background-color 0.1s;
  user-select: none;
}
.time-cell:hover {
  background-color: #e6f7ff;
}
.time-cell.active {
  background-color: #1890ff;
}
.slot-summary {
  padding: 12px 16px;
  border-top: 1px solid #e8eef2;
  background: #f8f9fa;
}
.summary-title {
  font-size: 12px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 8px;
}
.summary-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
.summary-item {
  font-size: 12px;
}
.summary-weekday {
  font-weight: 500;
  color: #606266;
}
.summary-hours {
  color: #1890ff;
}

/* ==================== 产品名列表 ==================== */
.product-name-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product-name-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ==================== 手机系列面板 ==================== */
.phone-series-panel {
  margin-top: 12px;
  padding: 14px 16px;
  background: #fafbfc;
  border-radius: 8px;
  border: 1px dashed #dcdfe6;
}

/* ==================== 文本域块 ==================== */
.textarea-block {
  width: 100%;
}

.content-textarea {
  max-width: 700px;
}

.error-list {
  margin-top: 8px;
  padding: 10px 14px;
  background: #fef0f0;
  border-radius: 6px;
  border: 1px solid #fde2e2;
}

.error-item {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.8;
}

.error-item i {
  margin-right: 4px;
}

/* ==================== 素材区域 ==================== */
.material-area {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.material-btn {
  padding: 10px 22px;
  font-size: 13px;
  border-radius: 8px;
}

.material-stats {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.material-tag {
  border-radius: 6px;
  font-size: 12px;
  padding: 6px 12px;
  background: #f0f5ff;
  border-color: #d0d9ff;
  color: #4158D0;
}

.material-tag i {
  margin-right: 3px;
}

.material-empty {
  color: #c0c4cc;
  font-size: 13px;
}

.material-empty i {
  margin-right: 4px;
}

/* ==================== 底部操作栏 ==================== */
.adgroup-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 950;
  padding: 16px 32px;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.footer-content .el-button {
  border-radius: 8px;
  font-size: 14px;
  padding: 10px 24px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* ==================== 对话框样式 ==================== */
.material-dialog>>>.el-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.material-dialog>>>.el-dialog__header {
  background: #fafbfc;
  border-bottom: 1px solid #e8eaed;
  padding: 18px 24px;
}

.material-dialog>>>.el-dialog__title {
  font-size: 17px;
  font-weight: 600;
  color: #303133;
}

.task-dialog>>>.el-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.tab-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #c0c4cc;
}

.tab-placeholder i {
  font-size: 48px;
  margin-bottom: 12px;
}

.tab-placeholder p {
  font-size: 14px;
}

.dialog-footer {
  text-align: right;
}

/* 任务详情卡片 */
.task-list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.task-detail-card {
  padding: 18px 20px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fafcfd;
}

.task-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.task-detail-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.task-detail-info {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

/* ==================== 全局微调 ==================== */
.el-radio-group {
  margin-right: 12px;
}

/* 美化 el-switch 的文字 */
.el-switch__label {
  font-size: 12px;
}

/* 美化 radio-button */
.el-radio-button__inner {
  border-radius: 6px !important;
}

/* 美化 checkbox-button */
.el-checkbox-button__inner {
  border-radius: 6px !important;
}

/* ==================== 滚动条美化 ==================== */
.task-list::-webkit-scrollbar,
.m-header::-webkit-scrollbar {
  width: 5px;
}

.task-list::-webkit-scrollbar-track,
.m-header::-webkit-scrollbar-track {
  background: transparent;
}

.task-list::-webkit-scrollbar-thumb,
.m-header::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

.task-list::-webkit-scrollbar-thumb:hover,
.m-header::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}

/* ==================== 响应式 ==================== */
@media (max-width: 1400px) {
  .input-large {
    width: 360px;
  }
}

@media (max-width: 768px) {
  .page-main {
    padding: 16px;
  }

  .plan-container {
    padding: 16px;
    border-radius: 8px;
  }

  .ad-flow {
    display: none;
  }

  .input-large,
  .input-medium,
  .input-short {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/vivo/vivopda/index.vue